import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { Badge } from "@/pages/legal/legalConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileSignature } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";
const lifecycleColor = {
  Draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "Awaiting Signature": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Negotiation: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  Executed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Expired: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Terminated: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export default function Contracts() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ customer_id: "", product_id: "", contract_type: "", template_id: "", template_version: "" });

  const { data: rows } = useQuery({ queryKey: ["legal-contracts"], queryFn: async () => (await api.get("/legal/contracts")).data });
  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const { data: templates } = useQuery({ queryKey: ["legal-templates"], queryFn: async () => (await api.get("/legal/templates")).data });
  const { data: meta } = useQuery({ queryKey: ["legal-meta"], queryFn: async () => (await api.get("/legal/meta")).data });
  const selTemplate = (templates || []).find((t) => t.id === f.template_id);

  const create = useMutation({
    mutationFn: async () => (await api.post("/legal/contracts", {
      ...f, template_version: selTemplate?.current_version || null,
    })).data,
    onSuccess: (c) => { qc.invalidateQueries({ queryKey: ["legal-contracts"] }); setOpen(false); toast.success(`Contract ${c.contract_ref} created`); navigate(`/legal/contracts/${c.id}`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div data-testid="legal-contracts-page">
      <PageHeader title="Contracts" subtitle="Customer contract records referencing your existing customers.">
        <Button onClick={() => setOpen(true)} data-testid="new-contract-btn" className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500">
          <Plus className="mr-1.5 h-4 w-4" /> New Contract
        </Button>
      </PageHeader>

      <div className="overflow-hidden rounded-xl border border-[#1E2D4A]">
        <table className="w-full text-sm">
          <thead className="bg-[#0E1729] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-[#1E2D4A]">
            {(rows || []).length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-contracts">No contracts yet.</td></tr>}
            {(rows || []).map((c) => (
              <tr key={c.id} className="bg-[#111A2E]/40 hover:bg-[#17233B]/60" data-testid={`contract-row-${c.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-cyan-300">{c.contract_ref}</td>
                <td className="px-4 py-3 text-slate-100"><FileSignature className="mr-1.5 inline h-3.5 w-3.5 text-cyan-400" />{c.customer_legal_name}</td>
                <td className="px-4 py-3 text-slate-400">{c.product_name || "—"}</td>
                <td className="px-4 py-3 text-slate-400">{c.contract_type}</td>
                <td className="px-4 py-3"><Badge value={c.lifecycle_status} map={lifecycleColor} testId={`contract-status-${c.id}`} /></td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" onClick={() => navigate(`/legal/contracts/${c.id}`)} data-testid={`open-contract-${c.id}`}>Open</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-lg" data-testid="new-contract-dialog">
          <DialogHeader><DialogTitle>New Contract</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs text-slate-400">Customer</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="contract-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                  {(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Product / Solution</Label>
                <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="contract-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    {(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Contract type</Label>
                <Select value={f.contract_type} onValueChange={(v) => setF({ ...f, contract_type: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="contract-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    {(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Base template (optional — links the version used)</Label>
              <Select value={f.template_id} onValueChange={(v) => setF({ ...f, template_id: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="contract-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                  {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.current_version})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !f.customer_id || !f.contract_type} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="contract-create-confirm">Create Draft Contract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
